import React, { useState } from 'react';
import { useAWSAuth } from '../../hooks/useAWSAuth';
import { awsAuthService } from '../../utils/awsAuth';
import { AWSStatusIndicator } from '../../components';
import styled from 'styled-components';


const CONVERSATION_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'ASK_ANYTHING', label: 'Ask Anything' },
  { value: 'JUZ_30', label: 'Juz 30' },
  { value: 'JOURNEY_CHAT', label: 'Journey Conversations' },
];

// Subscription status options (RevenueCat statuses)
const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'trial', label: 'Trial' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'cancelled_trial', label: 'Cancelled Trial' },
  { value: 'expired', label: 'Expired' },
  { value: 'grace_period_trial', label: 'Grace Period (Trial)' },
  { value: 'not_set', label: 'No Subscription' },
];

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h2`
  color: #222 !important;
  margin-bottom: 10px;
  font-weight: bold;
`;




const Input = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
`;

const Select = styled.select`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #333;
`;

const FilterHint = styled.small`
  color: #555;
  font-size: 12px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;


const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  background: ${props => props.variant === 'secondary' ? '#6c757d' : '#007bff'};
  color: white;
  
  &:hover {
    background: ${props => props.variant === 'secondary' ? '#5a6268' : '#0056b3'};
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
`;

const ConversationSection = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;


const DownloadSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const DownloadButton = styled(Button)`
  background: #28a745;
  font-size: 16px;
  padding: 15px 30px;
  
  &:hover {
    background: #218838;
  }
`;



export const ConversationExtraction: React.FC = () => {
  const { isAuthenticated } = useAWSAuth();
  const [downloadFormat, setDownloadFormat] = useState<'json' | 'csv'>('json');
  const [hoursAgo, setHoursAgo] = useState<number>(24);
  const [country, setCountry] = useState<string>('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('');
  const [conversationType, setConversationType] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  // Handle country input - auto capitalize and limit to 2 characters
  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
    setCountry(value);
  };


  const downloadConversations = async () => {
    if (!isAuthenticated) return;

    setIsDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(null);

    try {
      // Validate hours input
      if (hoursAgo <= 0 || hoursAgo > 8760) { // Max 1 year (365 * 24)
        setDownloadError('Hours must be between 1 and 8760 (1 year max)');
        return;
      }

      // Build request args
      const args: {
        format: string;
        hoursAgo: number;
        country?: string;
        subscriptionStatus?: string;
        conversationType?: string;
      } = {
        format: downloadFormat,
        hoursAgo: hoursAgo,
      };
      
      if (country) {
        args.country = country;
      }
      if (subscriptionStatus) {
        args.subscriptionStatus = subscriptionStatus;
      }
      if (conversationType) {
        args.conversationType = conversationType;
      }

      // Call the function via callFunction route
      const result = await awsAuthService.callFunction('websiteInternalFunctions/extractRecentConversations', args);
      
      // Prepare download content
      let content: string;
      let mimeType: string;
      
      if (downloadFormat === 'csv') {
        content = result.data || result;
        mimeType = 'text/csv';
      } else {
        content = JSON.stringify(result, null, 2);
        mimeType = 'application/json';
      }
      
      // Create blob and download
      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on format and current date
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `conversations_${timestamp}.${downloadFormat}`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
      setDownloadSuccess(`Successfully downloaded ${filename}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download conversations';
      setDownloadError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Conversation Extraction</Title>
      </Header>

      <AWSStatusIndicator />


      {isAuthenticated ? (
        <ConversationSection>
          <h3 style={{ color: '#222', fontWeight: 'bold' }}>Download Conversations</h3>
          <p>Extract conversation data from the database and download as a file.</p>

          {downloadError && <ErrorMessage>{downloadError}</ErrorMessage>}
          {downloadSuccess && <SuccessMessage>{downloadSuccess}</SuccessMessage>}

          <DownloadSection>
            <FilterGrid>
              {/* Time Range */}
              <FilterGroup>
                <FilterLabel htmlFor="hours-ago">Time Range (Hours Ago):</FilterLabel>
                <Input
                  id="hours-ago"
                  type="number"
                  min="1"
                  max="8760"
                  placeholder="e.g. 24 for last 24 hours"
                  value={hoursAgo}
                  onChange={(e) => setHoursAgo(parseInt(e.target.value) || 24)}
                />
                <FilterHint>Number of hours back from now (1-8760)</FilterHint>
              </FilterGroup>

              {/* Country Filter */}
              <FilterGroup>
                <FilterLabel htmlFor="country-filter">Country:</FilterLabel>
                <Input
                  id="country-filter"
                  type="text"
                  placeholder="e.g. US, GB, SA"
                  value={country}
                  onChange={handleCountryChange}
                  maxLength={2}
                  style={{ textTransform: 'uppercase' }}
                />
                <FilterHint>ISO 3166-1 alpha-2 code (e.g. US, GB, SA, AE)</FilterHint>
              </FilterGroup>

              {/* Subscription Status Filter */}
              <FilterGroup>
                <FilterLabel htmlFor="subscription-status">Subscription Status:</FilterLabel>
                <Select
                  id="subscription-status"
                  value={subscriptionStatus}
                  onChange={(e) => setSubscriptionStatus(e.target.value)}
                >
                  {SUBSCRIPTION_STATUS_OPTIONS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FilterHint>Filter by RevenueCat subscription status</FilterHint>
              </FilterGroup>

              {/* Conversation Type Filter */}
              <FilterGroup>
                <FilterLabel htmlFor="conversation-type">Conversation Type:</FilterLabel>
                <Select
                  id="conversation-type"
                  value={conversationType}
                  onChange={(e) => setConversationType(e.target.value)}
                >
                  {CONVERSATION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FilterHint>Filter by conversation type</FilterHint>
              </FilterGroup>

              {/* Download Format */}
              <FilterGroup>
                <FilterLabel htmlFor="download-format">Download Format:</FilterLabel>
                <Select
                  id="download-format"
                  value={downloadFormat}
                  onChange={(e) => setDownloadFormat(e.target.value as 'json' | 'csv')}
                >
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                </Select>
                <FilterHint>Select output file format</FilterHint>
              </FilterGroup>
            </FilterGrid>

            {/* Active Filters Summary */}
            {(country || subscriptionStatus || conversationType) && (
              <div style={{ 
                marginBottom: '20px', 
                padding: '10px 15px', 
                background: '#f0f0f0', 
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#333'
              }}>
                <strong style={{ color: '#222' }}>Active Filters:</strong>
                {country && <span style={{ marginLeft: '10px', color: '#444' }}>Country: {country}</span>}
                {subscriptionStatus && (
                  <span style={{ marginLeft: '10px', color: '#444' }}>
                    Subscription Status: {SUBSCRIPTION_STATUS_OPTIONS.find(s => s.value === subscriptionStatus)?.label}
                  </span>
                )}
                {conversationType && (
                  <span style={{ marginLeft: '10px', color: '#444' }}>
                    Type: {CONVERSATION_TYPE_OPTIONS.find(t => t.value === conversationType)?.label}
                  </span>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <DownloadButton onClick={downloadConversations} disabled={isDownloading}>
                {isDownloading ? (
                  <>
                    <LoadingSpinner style={{ marginRight: '10px' }} />
                    Downloading...
                  </>
                ) : (
                  `Download Conversations (${downloadFormat.toUpperCase()})`
                )}
              </DownloadButton>
            </div>
          </DownloadSection>
        </ConversationSection>
      ) : (
        <ConversationSection>
          <h3 style={{ color: '#222', fontWeight: 'bold' }}>Conversation Download Unavailable</h3>
          <ErrorMessage>
            AWS authentication is required to download conversations. 
            Please ensure your credentials work with both Realm and AWS systems.
          </ErrorMessage>
        </ConversationSection>
      )}
    </Container>
  );
};
